import styled, { css, keyframes } from "styled-components";
import { FaSpinner } from "react-icons/fa";
import { answerStateStyles, answerStateAttributes } from "../components/math/QuestionAnswerState";

export const PageShell = styled.div`
  width: min(1180px, 100%);
  margin: 18px auto 56px;
  padding: 0 12px;
  color: var(--app-text, #202124);
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0 14px;
  border-bottom: 1px solid var(--app-border, #dedede);

  @media (max-width: 720px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const TitleGroup = styled.div`
  display: grid;
  gap: 2px;
`;

export const Eyebrow = styled.span`
  color: var(--app-muted-text, #5f6368);
  font-size: 13px;
  font-weight: 700;
`;

export const PageTitle = styled.h2`
  margin: 0;
  color: var(--app-text, #202124);
  font-size: 26px;
  line-height: 1.25;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const CountText = styled.span`
  color: var(--app-subtle-text, #5f6368);
  font-size: 14px;
  font-weight: 700;
`;

export const TabBar = styled.div`
  display: flex;
  gap: 6px;
  padding: 14px 0;
  overflow-x: auto;
`;

export const TabButton = styled.button`
  flex-shrink: 0;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid
    ${({ $active, $variant }) =>
      $variant === "search"
        ? $active
          ? "#1f6b34"
          : "#99c9a4"
        : $active
          ? "var(--app-nav-active-bg, #222)"
          : "var(--app-border, #d7d7d7)"};
  background: ${({ $active, $variant }) =>
    $variant === "search"
      ? $active
        ? "#1f6b34"
        : "#eef9f0"
      : $active
        ? "var(--app-nav-active-bg, #222)"
        : "var(--app-nav-surface, #fff)"};
  color: ${({ $active, $variant }) =>
    $variant === "search"
      ? $active
        ? "#fff"
        : "#1f6b34"
      : $active
        ? "var(--app-nav-active-text, #fff)"
        : "var(--app-text, #333)"};
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
`;

export const Notice = styled.div`
  margin: 0 0 12px;
  padding: 10px 12px;
  border: 1px solid
    ${({ $tone }) => ($tone === "error" ? "var(--app-danger-border, #efb5b5)" : "var(--app-success-border, #a7d8b5)")};
  background: ${({ $tone }) => ($tone === "error" ? "var(--app-danger-bg, #fff4f4)" : "var(--app-success-bg, #f2fbf5)")};
  color: ${({ $tone }) => ($tone === "error" ? "var(--app-danger-text, #9f1d1d)" : "var(--app-success-text, #17642c)")};
  border-radius: 6px;
  font-weight: 700;
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const SpinningIcon = styled(FaSpinner)`
  animation: ${spin} 0.8s linear infinite;
`;

export const LoadingContainer = styled.div`
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LoadingText = styled.p`
  margin: 0;
  color: var(--app-muted-text, #5f6368);
  font-size: 1.5em;
  font-weight: 700;
  animation: ${bounce} 1.4s infinite;
`;

export const InlineLoading = styled.div`
  min-height: 142px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${LoadingText} {
    font-size: 1.15rem;
  }
`;

export const QuestionWorkspace = styled.div`
  min-width: 0;
  display: grid;
  gap: 14px;
`;

export const QuestionLookupSection = styled.section`
  min-width: 0;
  display: grid;
  gap: 12px;
  border-top: 2px solid var(--app-border, #202124);
  padding-top: 16px;
  margin-top: 4px;
`;

export const QuestionLookupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const SectionCount = styled.span`
  color: var(--app-subtle-text, #5f6368);
  font-size: 13px;
  font-weight: 800;
`;

export const FilterBar = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(220px, 1.25fr) repeat(8, minmax(96px, 0.9fr)) 40px;
  gap: 8px;
  align-items: center;

  > * {
    min-width: 0;
  }

  .answer-state-filter {
    grid-column: 1 / span 3;
    grid-row: 2;
  }

  .source-filter {
    grid-column: 9;
    grid-row: 1;
  }

  .family-filter {
    grid-column: 4 / span 3;
    grid-row: 2;
  }

  > button:last-child {
    grid-column: 10;
    grid-row: 1;
  }

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

    .answer-state-filter,
    .family-filter {
      grid-column: 1 / -1;
      grid-row: auto;
    }

    .source-filter,
    > button:last-child {
      grid-column: auto;
      grid-row: auto;
    }

    .answer-state-filter {
      width: 100%;
      overflow-x: auto;
    }

    > button:last-child {
      justify-self: end;
    }
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;

    > button:last-child {
      justify-self: start;
    }
  }
`;

export const SearchBox = styled.label`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  background: var(--app-input-bg, #fff);
  color: var(--app-muted-text, #5f6368);

  input {
    min-width: 0;
    width: 100%;
    border: 0;
    outline: 0;
    color: var(--app-input-text, #202124);
    font: inherit;
    background: transparent;
  }
`;

export const AnswerStateFilter = styled.div`
  min-width: 0;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 38px;
  padding: 4px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 7px;
  background: var(--app-input-bg, #fff);
`;

export const AnswerStateLabel = styled.span`
  padding: 0 8px;
  color: var(--app-muted-text, #6b7280);
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
`;

export const AnswerStateButton = styled.button`
  min-height: 28px;
  border: 1px solid
    ${({ $active }) => ($active ? "#b45309" : "transparent")};
  border-radius: 5px;
  padding: 0 10px;
  background: ${({ $active }) =>
    $active ? "#fff1d6" : "transparent"};
  color: ${({ $active }) =>
    $active ? "#7c2d12" : "var(--app-input-text, #202124)"};
  font: inherit;
  font-size: 13px;
  font-weight: 900;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: ${({ $active }) =>
      $active ? "#fff1d6" : "var(--app-surface-soft, #f4f5f7)"};
  }
`;

export const CompactSelect = styled.select`
  box-sizing: border-box;
  min-width: 0;
  width: 100%;
  min-height: 38px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  padding: 0 10px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  text-overflow: ellipsis;
`;

export const SplitLayout = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  align-items: start;
`;

export const TaxonomyGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 420px) 1fr;
  gap: 18px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const EditorPanel = styled.form`
  order: ${({ $panelOrder }) => $panelOrder || "initial"};
  min-width: 0;
  display: grid;
  gap: 14px;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 8px;
  padding: 14px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
`;

export const TablePanel = styled.div`
  order: ${({ $panelOrder }) => $panelOrder || "initial"};
  min-width: 0;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 8px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
  overflow: hidden;
`;

export const TaxonomyTableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 12px 0;
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const PanelTitleGroup = styled.div`
  min-width: 0;
  display: grid;
  gap: 6px;
`;

export const PanelTitle = styled.h3`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 17px;
  line-height: 1.35;
`;

export const InlineIdButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  max-width: 100%;
  min-height: 30px;
  padding: 0 8px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  background: var(--app-input-bg, #fff);
  color: var(--app-muted-text, #5f6368);
  cursor: pointer;
  font-family:
    source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 12px;
  font-weight: 700;
  overflow-wrap: anywhere;
`;

export const FieldGrid = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.label`
  min-width: 0;
  display: grid;
  gap: 6px;
  color: var(--app-subtle-text, #3c4043);
  font-size: 13px;
  font-weight: 700;

  input,
  select,
  textarea {
    box-sizing: border-box;
    min-width: 0;
    width: 100%;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    padding: 9px 10px;
    color: var(--app-input-text, #202124);
    font: inherit;
    font-weight: 500;
    background: var(--app-input-bg, #fff);

    &::placeholder {
      color: var(--app-muted-text, #8b929e);
      opacity: 1;
    }
  }

  textarea {
    resize: vertical;
    min-height: 96px;
  }
`;

export const FieldHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;

  > label {
    min-width: 0;
  }

  > div {
    min-width: 0;
  }

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-wrap: wrap;

    > div {
      width: 100%;
      justify-content: flex-start;
    }
  }
`;


export const InlineToolButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 0 9px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;

  &:hover {
    background: var(--app-surface-soft, #f4f5f7);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export const TwoColumnText = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const ChoiceOptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const ChoiceOptionRow = styled.label`
  min-width: 0;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  padding: 8px;
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 7px;
  background: var(--app-surface-soft, #f7f8fa);

  strong {
    color: var(--app-subtle-text, #3c4043);
    font-size: 13px;
    line-height: 34px;
    text-align: center;
  }

  textarea {
    min-height: 42px;
    resize: vertical;
  }
`;

export const QuestionPreviewPanel = styled.section`
  min-width: 0;
  display: grid;
  gap: 10px;
  border: 1px solid var(--app-border, #d8dde3);
  border-radius: 8px;
  padding: 12px;
  background: var(--app-surface-soft, #f3f5f7);
`;

export const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const PreviewGrid = styled.div`
  min-width: 0;
  display: grid;
  gap: 0;
  border: 1px solid var(--app-border, #d1d7de);
  border-radius: 8px;
  overflow: hidden;
  background: var(--app-surface, #eef1f4);
`;

export const PreviewBlock = styled.div.attrs((props) =>
  props.$tone === "answer" ? answerStateAttributes(props) : {},
)`
  min-width: 0;
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 12px;
  padding: 14px;
  border-bottom: 1px solid var(--app-border, #d7dde4);
  background: var(--app-surface, #f9fafb);

  &:nth-child(odd) {
    background: var(--app-surface-hover, #f1f4f7);
  }

  &:last-child {
    border-bottom: 0;
  }

  ${({ $tone }) => $tone === "answer" && css`&& { ${answerStateStyles} }`}

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

export const PreviewLabel = styled.div`
  color: var(--answer-label-color, var(--app-subtle-text, #374151));
  font-size: 13px;
  font-weight: 900;
`;

export const PreviewContent = styled.div`
  min-width: 0;
  overflow: visible;
  color: ${({ $empty }) =>
    $empty ? "var(--app-muted-text, #8b929e)" : "var(--app-surface-text, #202124)"};
  font-weight: ${({ $empty }) => ($empty ? 600 : 700)};
  line-height: 1.65;
  overflow-wrap: anywhere;
  text-align: left;
  white-space: pre-wrap;

  .katex {
    font-size: 1em;
  }

  .katex-display {
    margin: 5px 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

export const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  color: var(--app-subtle-text, #3c4043);
  font-size: 13px;
  font-weight: 700;

  input {
    width: 18px;
    height: 18px;
  }
`;

export const AssetSection = styled.div`
  min-width: 0;
  display: grid;
  gap: 10px;
  border-top: 1px solid var(--app-border, #e8eaed);
  padding-top: 12px;
`;

export const AssetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const AssetList = styled.div`
  min-width: 0;
  display: grid;
  gap: 8px;
`;

export const AssetRow = styled.div`
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--app-border, #e1e5eb);
  border-radius: 14px;
  background: var(--app-surface, #fff);
  box-shadow: 0 2px 8px rgb(20 35 55 / 4%);
`;

export const AssetMetadata = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 18px;
  border-bottom: 1px solid var(--app-border, #e1e5eb);
  background: var(--app-surface-hover, #f7f9fc);

  > strong {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-right: auto;
    white-space: nowrap;
    font-size: 14px;
    color: var(--app-text, #263348);
    svg { color: var(--app-muted-text, #758299); }
  }

  > label {
    display: flex;
    align-items: center;
    gap: 8px;
    select { width: 100px; }
    input { width: 68px; }
  }

  > button { color: var(--app-muted-text, #758299); }
  > button:hover { color: var(--app-danger-text, #b42318); }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px;
    > strong { width: 100%; }
    > button { margin-left: auto; }
  }
`;

export const AssetWorkspace = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  align-items: stretch;
  gap: 18px;
  padding: 18px;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 12px;
    gap: 14px;
  }
`;

export const AssetCodeToolbar = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;

  > label {
    display: flex;
    align-items: center;
    gap: 8px;
    select { width: 110px; }
  }
  > div { display: flex; gap: 6px; flex-wrap: wrap; }
  button { font-size: 12px; padding: 7px 9px; }
`;

export const AssetDetails = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
  gap: 14px;
  padding: 16px 18px;
  border-top: 1px solid var(--app-border, #e1e5eb);
  background: var(--app-surface-hover, #f7f9fc);

  > label { align-content: start; }
  textarea { min-height: 66px; font-weight: 400; }
  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
    padding: 12px;
  }
`;

export const AssetPreviewGrid = styled.div`
  grid-column: 1 / -1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${({ $compact }) => ($compact ? "5px" : "8px")};
  align-items: flex-start;
  margin-top: ${({ $compact }) => ($compact ? "0" : "2px")};
`;

export const AssetPreviewCard = styled.figure`
  width: ${({ $compact }) => ($compact ? "58px" : "min(220px, 100%)")};
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 7px;
  background: var(--app-input-bg, #fff);
`;

export const AssetPreviewImage = styled.img`
  display: block;
  width: 100%;
  max-width: 100%;
  height: ${({ $compact }) => ($compact ? "58px" : "auto")};
  max-height: ${({ $compact }) => ($compact ? "58px" : "180px")};
  object-fit: contain;
  background: #fff;
`;

export const AssetPreviewMeta = styled.figcaption`
  padding: 5px 7px;
  border-top: 1px solid var(--app-border, #e5e7eb);
  color: var(--app-muted-text, #5f6368);
  font-size: 11px;
  font-weight: 800;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

export const AssetSourcePanel = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  > label { flex: 1; grid-template-rows: auto 1fr; }
  > label:has(input) { flex: 0; grid-template-rows: auto auto; }
  > p { margin: 0; color: var(--app-muted-text, #64748b); font-size: 12px; line-height: 1.7; }
  textarea {
    min-height: 240px;
    height: 100%;
    padding: 14px;
    background: var(--app-input-bg, #fbfcfe);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 12px;
    line-height: 1.75;
    font-weight: 400;
    tab-size: 4;
  }
`;

export const AssetCodePreviewPane = styled.div`
  min-height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 10px;
`;

export const AssetCodePreviewStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid
    ${({ $status }) => ($status === "error" ? "#efb5b5" : "#d5d7db")};
  border-radius: 8px;
  padding: 9px 10px;
  background: ${({ $status }) =>
    $status === "error" ? "#fff4f4" : "var(--app-surface-soft, #f7f8fa)"};
  color: ${({ $status }) =>
    $status === "error" ? "#9f1d1d" : "var(--app-subtle-text, #3d3d3d)"};
  font-size: 13px;
  font-weight: 800;
`;

export const AssetCodePreviewFrame = styled.div`
  min-height: 240px;
  min-width: 0;
`;

export const FormActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const DuplicateWarningPanel = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 12px;
  border: 1px solid #e8c36a;
  border-radius: 6px;
  background: var(--app-warning-bg, #fff8e5);
  color: var(--app-warning-text, #4f3a05);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const DuplicateWarningText = styled.div`
  display: grid;
  gap: 5px;
  min-width: 0;
  font-weight: 800;

  span {
    color: var(--app-warning-text, #6b560f);
    font-size: 13px;
  }
`;

export const DuplicateActionRow = styled.div`
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

export const IconTextButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 12px;
  border: 1px solid
    ${({ $danger, $primary, $tone }) =>
      $danger
        ? "#b42318"
        : $tone === "status"
          ? "#7aa7e6"
          : $tone === "visibility"
            ? "#7fbe8f"
        : $primary
          ? "var(--app-nav-active-bg, #202124)"
          : "var(--app-border, #d5d7db)"};
  background: ${({ $danger, $primary, $tone }) =>
    $danger
      ? "#c0392b"
      : $tone === "status"
        ? "#eaf3ff"
        : $tone === "visibility"
          ? "#eef9f0"
      : $primary
        ? "var(--app-nav-active-bg, #202124)"
        : "var(--app-input-bg, #fff)"};
  color: ${({ $danger, $primary, $tone }) =>
    $danger
      ? "#fff"
      : $tone === "status"
        ? "#1d4f91"
        : $tone === "visibility"
          ? "#1f6b34"
      : $primary
        ? "var(--app-nav-active-text, #fff)"
        : "var(--app-input-text, #202124)"};
  border-radius: 6px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const IconOnlyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--app-border, #d5d7db);
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: var(--app-surface-soft, #f4f5f7);
  }
`;

export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

export const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 620px;

  th,
  td {
    padding: 11px 12px;
    border-bottom: 1px solid var(--app-border, #eceff1);
    text-align: left;
    vertical-align: top;
  }

  th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--app-surface-soft, #f8f9fa);
    color: var(--app-muted-text, #5f6368);
    font-size: 12px;
    text-transform: uppercase;
    white-space: nowrap;
  }

  td {
    color: var(--app-surface-text, #202124);
    font-size: 14px;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  tbody tr[data-question-preview="true"] {
    cursor: zoom-in;
  }

  tbody tr[data-question-preview="true"]:focus-visible {
    outline: 2px solid var(--app-nav-active-bg, #202124);
    outline-offset: -2px;
  }

  tbody tr[data-selected="true"] td {
    background: color-mix(in srgb, var(--app-surface, #fff) 92%, #2563eb 8%);
  }

  tbody tr[data-selected="true"] td:first-child {
    box-shadow: inset 3px 0 #2563eb;
  }

  [data-auth-theme="night"] & tbody tr[data-selected="true"] td {
    background: var(--app-surface-soft, #333333);
  }
`;

export const QuestionText = styled.div`
  max-width: 620px;
  max-height: 260px;
  margin-top: 6px;
  overflow: auto;
  color: var(--app-surface-text, #202124);
  font-weight: 700;
  line-height: 1.5;
  overflow-wrap: anywhere;
  white-space: pre-wrap;

  .katex {
    font-size: 1em;
  }

  .katex-display {
    margin: 4px 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

export const QuestionDetailBlock = styled.div.attrs((props) =>
  props.$tone === "answer" ? answerStateAttributes(props) : {},
)`
  max-width: 620px;
  margin-top: 10px;
  border: 1px solid var(--app-border, #e5e7eb);
  border-left: 3px solid
    ${({ $tone }) => ($tone === "answer" ? "#e3b341" : "#9aa0a6")};
  border-radius: 6px;
  padding: 10px 12px;
  background: ${({ $tone }) =>
    $tone === "answer"
      ? "color-mix(in srgb, var(--app-question-card-bg, #fff) 88%, #e3b341 12%)"
      : "var(--app-question-detail-bg, #f7f8fa)"};
  color: var(--app-surface-text, #202124);
  opacity: ${({ $empty, $tone }) => ($empty && $tone !== "answer" ? 0.72 : 1)};
  ${({ $tone }) => $tone === "answer" && answerStateStyles}
`;

export const QuestionDetailLabel = styled.div`
  margin-bottom: 4px;
  color: ${({ $tone }) =>
    $tone === "answer"
      ? "var(--answer-label-color)"
      : "var(--app-muted-text, #5f6368)"};
  font-size: 12px;
  font-weight: 900;
`;

export const QuestionDetailText = styled.div`
  max-height: 220px;
  overflow: auto;
  color: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.55;
  overflow-wrap: anywhere;
  text-align: left;
  white-space: pre-wrap;

  .katex {
    color: inherit;
    font-size: 1em;
  }

  .katex-display {
    margin: 4px 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

export const QuestionListFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 620px;
  min-height: 46px;
  border-top: 1px solid var(--app-border, #eceff1);
  color: var(--app-subtle-text, #5f6368);
  font-size: 13px;
  font-weight: 800;

  ${SpinningIcon} {
    font-size: 14px;
  }
`;

export const QuestionIdLine = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--app-muted-text, #6b7280);
  font-family:
    source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 12px;
  font-weight: 700;
`;

export const QuestionMetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
  color: var(--app-muted-text, #5f6368);
  font-size: 12px;
  line-height: 1.35;
  overflow-wrap: anywhere;
`;

export const SmallIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 6px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #3c4043);
  cursor: pointer;

  &:hover {
    background: var(--app-surface-soft, #f4f5f7);
  }
`;

export const SmallMuted = styled.div`
  margin-top: 3px;
  color: var(--app-muted-text, #6b7280);
  font-size: 12px;
`;

export const AuthorBadge = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 6px;
  border-radius: 6px;
  background: ${({ $source }) =>
    $source === "api_key" ? "#f1f6ee" : "#eef2f7"};
  color: ${({ $source }) => ($source === "api_key" ? "#33691e" : "#3c4043")};
  font-size: 11px;
  font-weight: 800;
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--app-surface-soft, #eef2f7);
  color: var(--app-surface-text, #202124);
  font-size: 12px;
  font-weight: 800;
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: fit-content;
  min-height: 26px;
  padding: 0 9px;
  border: 1px solid
    ${({ $status }) =>
      $status === "published"
        ? "#9dbbe8"
        : $status === "archived"
          ? "#c7b3e6"
          : "#cfd4dc"};
  border-radius: 999px;
  background: ${({ $status }) =>
    $status === "published"
      ? "#eff6ff"
      : $status === "archived"
        ? "#f5f0ff"
        : "#f8f9fa"};
  color: ${({ $status }) =>
    $status === "published"
      ? "#1d4f91"
      : $status === "archived"
        ? "#5b2c83"
        : "#4b5563"};
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;

  svg {
    flex: 0 0 auto;
    font-size: 11px;
  }
`;

export const GoodQuestionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: fit-content;
  min-height: 24px;
  padding: 0 8px;
  border: 1px solid #e1b84f;
  border-radius: 999px;
  background: #fff7d6;
  color: #8a5a00;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;

  svg {
    flex: 0 0 auto;
    font-size: 11px;
  }
`;

export const VisibilityBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: fit-content;
  min-height: 28px;
  padding: 0 10px;
  border: 1px solid
    ${({ $visibility }) =>
      $visibility === "private" ? "#e6a7a7" : "#99c9a4"};
  border-radius: 999px;
  background: ${({ $visibility }) =>
    $visibility === "private" ? "#fff1f1" : "#eef9f0"};
  color: ${({ $visibility }) =>
    $visibility === "private" ? "#9b1c1c" : "#1f6b34"};
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;

  svg {
    flex: 0 0 auto;
    font-size: 11px;
  }
`;

export const RowActions = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  flex-wrap: wrap;

  ${DataTable} & {
    flex-wrap: nowrap;
    width: max-content;
    margin-left: auto;
  }

  ${DataTable} & > button {
    flex: 0 0 auto;
  }
`;

export const BulkActionBar = styled.div`
  display: grid;
  gap: 12px;
  min-width: 0;
  padding: 14px;
  border-bottom: 1px solid var(--app-border, #d8d8d8);
  background: ${({ $active }) => $active
    ? "color-mix(in srgb, var(--app-surface, #fff) 96%, #2563eb 4%)"
    : "var(--app-surface-soft, #f4f6f8)"};

  ${SmallMuted} {
    margin: -4px 0 0;
  }

  [data-auth-theme="night"] & {
    background: var(--app-surface-soft, #333333);
  }
`;

export const BulkSelectionSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  ${IconTextButton} {
    min-height: 44px;
  }

  ${CountText} {
    flex: 1 1 200px;
  }
`;

export const BulkSelectionControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 560px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

export const BulkActionGroup = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  min-width: 0;
  border-top: 1px solid var(--app-border, #d8d8d8);
  padding-top: 12px;

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const BulkActionField = styled.div`
  display: grid;
  grid-template-columns: auto minmax(104px, 1fr) auto;
  align-items: center;
  gap: 8px;
  flex: 1 1 300px;
  min-width: 0;

  label {
    color: var(--app-muted-text, #5f6368);
    font-size: 13px;
    font-weight: 700;
  }

  ${CompactSelect} {
    min-width: 0;
    width: 100%;
    min-height: 44px;
  }

  button {
    min-height: 44px;
    white-space: nowrap;
  }

  @media (max-width: 560px) {
    flex: none;
    grid-template-columns: minmax(0, 1fr) auto;

    label {
      grid-column: 1 / -1;
    }
  }
`;

export const BulkDangerAction = styled.div`
  border-left: 1px solid var(--app-border, #d8d8d8);
  padding-left: 12px;

  button {
    min-height: 44px;
    white-space: nowrap;
  }

  @media (max-width: 560px) {
    border-left: 0;
    border-top: 1px solid var(--app-border, #d8d8d8);
    padding: 12px 0 0;
    button {
      width: 100%;
    }
  }
`;

export const CheckboxCell = styled.td`
  width: 76px;
  text-align: center;
  vertical-align: top;
`;

export const QuestionSelectionLabel = styled.label`
  display: grid;
  justify-items: center;
  align-content: center;
  gap: 5px;
  min-width: 44px;
  min-height: 52px;
  padding: 6px;
  border-radius: 6px;
  color: ${({ $selected }) => $selected
    ? "var(--app-surface-text, #202124)"
    : "var(--app-muted-text, #5f6368)"};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--app-surface, #fff) 90%, #2563eb 10%);
  }

  [data-auth-theme="night"] &:hover {
    background: var(--app-surface-hover, #424242);
  }

  &:focus-within {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }

  input {
    width: 20px;
    height: 20px;
    margin: 0;
    accent-color: #2563eb;
    cursor: pointer;
  }

  &:has(input:disabled) {
    cursor: not-allowed;
    opacity: 0.6;
  }

  input:disabled {
    cursor: not-allowed;
  }
`;

export const EmptyLine = styled.div`
  padding: 16px;
  color: var(--app-muted-text, #6b7280);
  font-weight: 700;
  text-align: center;
`;

export const ApiGuidePanel = styled.section`
  display: grid;
  gap: 14px;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 8px;
  padding: 16px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
`;

export const ApiGuideIntro = styled.p`
  max-width: 860px;
  margin: 0;
  color: var(--app-muted-text, #4b5563);
  font-size: 14px;
  font-weight: 650;
  line-height: 1.7;
`;

export const ApiStepList = styled.ol`
  display: grid;
  gap: 6px;
  margin: 0;
  padding-left: 22px;
  color: var(--app-subtle-text, #30343a);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.6;
`;

export const ApiKeyField = styled.label`
  display: grid;
  gap: 6px;
  max-width: 720px;
  color: var(--app-subtle-text, #30343a);
  font-size: 13px;
  font-weight: 800;

  input {
    min-height: 40px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 6px;
    padding: 0 10px;
    color: var(--app-input-text, #202124);
    background: var(--app-input-bg, #fff);
    font: inherit;
    font-family:
      source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
    font-weight: 650;
  }

  span {
    color: var(--app-muted-text, #6b7280);
    font-size: 12px;
    font-weight: 700;
  }
`;

export const ApiGuideGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const ApiGuideCard = styled.div`
  min-width: 0;
  display: grid;
  gap: 10px;
  border: 1px solid var(--app-border, #e0e3e7);
  border-radius: 8px;
  padding: 12px;
  background: var(--app-surface-hover, #fbfcfd);
`;

export const CodeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--app-surface-text, #202124);
    font-weight: 900;
  }
`;

export const ApiCodeBlock = styled.pre`
  margin: 0;
  max-height: 420px;
  overflow: auto;
  border: 1px solid var(--app-border, #e0e3e7);
  border-radius: 8px;
  padding: 12px;
  background: var(--app-input-bg, #f8f9fa);
  color: var(--app-input-text, #202124);
  font-family:
    source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

export const BulkImportGrid = styled.div`
  display: grid;
  gap: 14px;
`;

export const BulkImportPanel = styled.section`
  min-width: 0;
  display: grid;
  gap: 14px;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 8px;
  padding: 16px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
`;

export const BulkImportSource = styled.label`
  min-width: 0;
  display: grid;
  gap: 8px;
  color: var(--app-subtle-text, #3c4043);
  font-size: 13px;
  font-weight: 800;

  textarea {
    box-sizing: border-box;
    width: 100%;
    min-height: 220px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 8px;
    padding: 12px;
    background: var(--app-input-bg, #fff);
    color: var(--app-input-text, #202124);
    font-family:
      source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.65;
    resize: vertical;
  }
`;

export const BulkWordDropZone = styled.button`
  min-width: 0;
  min-height: 116px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 7px;
  border: 2px dashed
    ${({ $dragOver }) => ($dragOver ? "#202124" : "var(--app-border, #b8c0cc)")};
  border-radius: 8px;
  padding: 16px;
  background: ${({ $dragOver }) =>
    $dragOver ? "var(--app-surface-soft, #eef2f7)" : "var(--app-input-bg, #fff)"};
  color: var(--app-muted-text, #5f6368);
  cursor: pointer;
  text-align: center;

  svg {
    color: var(--app-surface-text, #202124);
    font-size: 28px;
  }

  strong {
    color: var(--app-surface-text, #202124);
    font-size: 15px;
  }

  span {
    font-size: 13px;
    font-weight: 700;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.66;
  }
`;

export const BulkWordProgress = styled.div`
  width: 100%;
  display: grid;
  gap: 6px;
`;

export const BulkWordProgressPanel = styled.div`
  width: min(460px, 100%);
  display: grid;
  gap: 9px;
  margin-top: 6px;
  border: 1px solid
    ${({ $status }) =>
      $status === "error"
        ? "#e2b6b0"
        : $status === "success"
          ? "#a8d7b4"
          : "var(--app-border, #d8d8d8)"};
  border-radius: 8px;
  padding: 11px 12px;
  background: ${({ $status }) =>
    $status === "error"
      ? "#fff4f2"
      : $status === "success"
        ? "#f1f8f3"
        : "var(--app-surface-soft, #f4f6f8)"};
  color: var(--app-subtle-text, #303134);
  text-align: left;
`;

export const BulkWordProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const BulkWordProgressTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  svg {
    flex: 0 0 auto;
    color: var(--app-surface-text, #202124);
    font-size: 14px;
  }

  strong {
    color: var(--app-text, #202124);
    font-size: 14px;
  }

  ${BulkWordProgressPanel}[data-status="loading"] & svg {
    animation: bulkWordProgressSpin 0.9s linear infinite;
  }

  @keyframes bulkWordProgressSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const BulkWordProgressMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 58%;
  color: var(--app-muted-text, #5f6368);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  span {
    flex: 0 0 auto;
    border-radius: 999px;
    padding: 3px 7px;
    background: var(--app-input-bg, #fff);
    color: var(--app-subtle-text, #3d3d3d);
  }

  @media (max-width: 640px) {
    max-width: 100%;
  }
`;

export const BulkWordProgressFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  span {
    min-width: 0;
    color: var(--app-muted-text, #5f6368);
    font-size: 12px;
    font-weight: 800;
    overflow-wrap: anywhere;
  }
`;

export const BulkWordProgressBar = styled.div`
  width: 100%;
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(32, 33, 36, 0.12);

  span {
    display: block;
    height: 100%;
    min-width: 6px;
    border-radius: inherit;
    background: ${({ $active }) =>
      $active
        ? "linear-gradient(90deg, #256f46 0%, #53a46f 50%, #256f46 100%)"
        : "#256f46"};
    transition: width 180ms ease;
    ${({ $indeterminate }) =>
      $indeterminate
        ? `
          animation: bulkWordProgressPulse 1.1s ease-in-out infinite;
          transform-origin: left center;
        `
        : ""}
  }

  @keyframes bulkWordProgressPulse {
    0% {
      transform: translateX(-40%) scaleX(0.75);
    }
    50% {
      transform: translateX(230%) scaleX(1.25);
    }
    100% {
      transform: translateX(560%) scaleX(0.75);
    }
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const BulkImportToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  border-top: 1px solid var(--app-border, #e5e7eb);
  padding-top: 12px;
`;

export const BulkDraftToolbar = styled(BulkImportToolbar)`
  align-items: stretch;
  flex-direction: column;

  > ${BulkActionGroup} {
    border-top: 0;
    padding-top: 0;
  }

  ${CompactSelect} {
    flex: 0 1 360px;
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 560px) {
    ${CompactSelect} {
      flex: 0 1 auto;
    }
  }
`;

export const BulkProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  color: var(--app-muted-text, #5f6368);
  font-size: 12px;
  font-weight: 800;

  strong,
  span {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 999px;
    padding: 0 10px;
    background: var(--app-input-bg, #fff);
  }

  strong {
    color: var(--app-surface-text, #202124);
  }
`;

export const BulkQuestionList = styled.div`
  display: grid;
  gap: 10px;
`;

export const BulkQuestionCard = styled.article`
  min-width: 0;
  display: grid;
  gap: 10px;
  border: 1px solid
    ${({ $status }) =>
      $status === "success"
        ? "#99c9a4"
        : $status === "duplicate"
          ? "#e8c36a"
          : $status === "error"
            ? "#efb5b5"
            : "var(--app-border, #dedede)"};
  border-radius: 8px;
  padding: 12px;
  color: var(--app-surface-text, #202124);
  background: ${({ $status }) =>
    $status === "success"
      ? "color-mix(in srgb, var(--app-question-card-bg, #fff) 92%, #16a34a 8%)"
      : $status === "duplicate"
        ? "color-mix(in srgb, var(--app-question-card-bg, #fff) 92%, #e3b341 8%)"
        : $status === "error"
          ? "color-mix(in srgb, var(--app-question-card-bg, #fff) 92%, #dc2626 8%)"
          : "var(--app-question-card-bg, #fff)"};
  cursor: zoom-in;

  &:focus-visible {
    outline: 2px solid var(--app-nav-active-bg, #202124);
    outline-offset: 3px;
  }
`;

export const BulkQuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const BulkQuestionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  color: var(--app-muted-text, #5f6368);
  font-size: 12px;
  font-weight: 800;

  span:not([data-difficulty]),
  strong {
    min-height: 24px;
    border: 1px solid var(--app-border, #d5d7db);
    border-radius: 999px;
    padding: 0 8px;
    background: var(--app-input-bg, #fff);
  }

  strong {
    color: var(--app-surface-text, #202124);
  }
`;

export const BulkQuestionBody = styled.div`
  min-width: 0;
  display: grid;
  gap: 0;
  border-top: 1px solid var(--app-border, #edf0f2);
  padding-top: 8px;

  ${QuestionText},
  ${QuestionDetailBlock} {
    max-width: none;
  }

  ${QuestionText} {
    margin-top: 0;
  }
`;

export const BulkQuestionFlags = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const BulkFlag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === "error"
        ? "#efb5b5"
        : $tone === "duplicate"
          ? "#e8c36a"
          : $tone === "success"
            ? "#99c9a4"
            : "#d5d7db"};
  border-radius: 999px;
  padding: 0 8px;
  background: ${({ $tone }) =>
    $tone === "error"
      ? "#fff4f4"
      : $tone === "duplicate"
        ? "#fff8e5"
        : $tone === "success"
          ? "#f2fbf5"
          : "var(--app-surface-soft, #f4f5f7)"};
  color: ${({ $tone }) =>
    $tone === "error"
      ? "#9f1d1d"
      : $tone === "duplicate"
        ? "#6b560f"
        : $tone === "success"
          ? "#17642c"
          : "var(--app-subtle-text, #3c4043)"};
  font-size: 12px;
  font-weight: 900;
`;

export const DuplicateScanPanel = styled.section`
  display: grid;
  gap: 14px;
  border: 1px solid var(--app-border, #dedede);
  border-radius: 8px;
  padding: 14px;
  background: var(--app-surface, #fff);
`;

export const DuplicateScanToolbar = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(220px, 1.5fr) repeat(3, minmax(140px, 0.8fr)) auto;
  gap: 8px;
  align-items: center;

  > * {
    min-width: 0;
  }

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);

    > button:last-child {
      grid-column: 1 / -1;
      justify-self: start;
    }
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const DuplicateScanSummary = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  color: var(--app-muted-text, #5f6368);
  font-size: 13px;
  font-weight: 800;
`;

export const DuplicateReviewNavigation = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

export const DuplicateScanProgress = styled.div`
  display: grid;
  gap: 9px;
  padding: 12px;
  border: 1px solid #a7c9ee;
  border-radius: 7px;
  background: var(--app-surface-soft, #f2f7ff);
  color: var(--app-surface-text, #202124);
  font-size: 13px;
`;

export const DuplicateScanProgressBar = styled.progress`
  display: block;
  width: 100%;
  height: 12px;
  accent-color: #3875d7;
`;

export const DuplicateHistoryButton = styled(IconOnlyButton)`
  width: auto;
  min-width: 38px;
  gap: 6px;
  padding: 0 10px;
  border-color: ${({ $active }) => $active ? "#b45309" : "var(--app-border, #d5d7db)"};
  background: ${({ $active }) => $active ? "#fff1d6" : "var(--app-input-bg, #fff)"};
  color: ${({ $active }) => $active ? "#7c2d12" : "var(--app-input-text, #202124)"};
  font-weight: 800;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const DuplicatePairList = styled.div`
  display: grid;
  gap: 12px;
`;

export const DuplicatePairCard = styled.article`
  display: grid;
  gap: 10px;
  border: 1px solid ${({ $level }) => ($level === "high" ? "#e7a7a7" : "#e8c36a")};
  border-radius: 8px;
  padding: 12px;
  background: ${({ $level }) => ($level === "high" ? "#fff8f8" : "#fffdf5")};
`;

export const DuplicatePairHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

export const DuplicateScore = styled.strong`
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  border-radius: 999px;
  padding: 0 10px;
  background: ${({ $level }) => ($level === "high" ? "#9f1d1d" : "#8a6500")};
  color: #fff;
  font-size: 13px;
`;

export const DuplicatePairGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

export const DuplicateQuestionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;

  > :first-child {
    min-width: 0;
    margin-top: 0;
  }
`;

export const DuplicateDeleteButton = styled(SmallIconButton)`
  flex: 0 0 auto;
  color: #c0392b;
  font-size: 11px;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

export const DuplicateQuestionCard = styled.div`
  min-width: 0;
  display: grid;
  gap: 8px;
  border: 1px solid var(--app-border, #d5d7db);
  border-radius: 7px;
  padding: 11px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: #3875d7;
    box-shadow: 0 0 0 2px rgba(56, 117, 215, 0.12);
    outline: 0;
  }
`;

export const BulkModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: grid;
  place-items: center;
  padding: 18px;
  background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));
`;

export const BulkModal = styled.div`
  width: min(980px, 100%);
  max-height: min(92vh, 920px);
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: 14px;
  border-radius: 8px;
  padding: 16px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.28);
`;

export const BulkModalBody = styled.div`
  min-height: 0;
  min-width: 0;
  display: grid;
  gap: 14px;
  overflow: auto;
  padding-right: 4px;
`;

export const BulkModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid var(--app-border, #e5e7eb);
  padding-top: 12px;
`;
